import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { api } from '@/lib/api';
import { queryKeys } from '@/hooks/queryKeys';
import { useNotify } from '@/hooks/useNotify';
import type { CustomerSignatureMode, CustomerSignatureSettings } from '@/types';
import {
  buildCustomerSignaturePayload,
  DEFAULT_CUSTOMER_SIGNATURE_FORM,
  formStateFromSettings,
  type CustomerSignatureFormState,
} from './buildCustomerSignaturePayload';

interface TenantResponse {
  id: string;
  name: string;
  settings: {
    customerSignature?: CustomerSignatureSettings;
  };
}

const MODE_OPTIONS: { value: CustomerSignatureMode; labelKey: string }[] = [
  { value: 'never', labelKey: 'customerSignature.modeNever' },
  { value: 'always', labelKey: 'customerSignature.modeAlways' },
  { value: 'conditional', labelKey: 'customerSignature.modeConditional' },
];

export function CustomerSignatureSettingsForm() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CustomerSignatureFormState>(DEFAULT_CUSTOMER_SIGNATURE_FORM);
  const originalConsentText = useRef('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [thresholdError, setThresholdError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<TenantResponse>('/tenants/me')
      .then((tenant) => {
        if (cancelled) return;
        const next = formStateFromSettings(tenant.settings.customerSignature);
        originalConsentText.current = next.consentText;
        setForm(next);
        setLoadError(false);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const patchForm = (patch: Partial<CustomerSignatureFormState>) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const consentWillIncrement = form.consentText !== originalConsentText.current;
  const nextConsentVersion = consentWillIncrement
    ? form.consentTextVersion + 1
    : form.consentTextVersion;
  const conditional = form.mode === 'conditional';

  const handleSave = async () => {
    const built = buildCustomerSignaturePayload(form, originalConsentText.current);
    if (!built.ok) {
      setThresholdError(true);
      notify.error(t('error'), t('customerSignature.thresholdInvalid'));
      return;
    }
    setThresholdError(false);
    setSaving(true);
    try {
      await api.patch('/tenants/me/settings', { customerSignature: built.payload });
      originalConsentText.current = form.consentText;
      setForm((current) => ({
        ...current,
        consentTextVersion: built.payload.consentTextVersion ?? current.consentTextVersion,
      }));
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant.all });
      notify.success(t('settingsSaved'), t('customerSignature.savedDesc'));
    } catch (error) {
      notify.error(t('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="customer-signature-settings">
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('customerSignature.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('customerSignature.description')}
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('customerSignature.loadFailed')}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
        <Stack spacing={3}>
          <FormControl fullWidth disabled={loading}>
            <InputLabel id="customer-signature-mode-label">{t('customerSignature.mode')}</InputLabel>
            <Select
              labelId="customer-signature-mode-label"
              label={t('customerSignature.mode')}
              value={form.mode}
              onChange={(event) => patchForm({ mode: event.target.value as CustomerSignatureMode })}
            >
              {MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t('customerSignature.modeHint')}</FormHelperText>
          </FormControl>

          <TextField
            label={t('customerSignature.threshold')}
            value={form.thresholdEuros}
            onChange={(event) => {
              setThresholdError(false);
              patchForm({ thresholdEuros: event.target.value });
            }}
            disabled={loading || !conditional}
            error={thresholdError}
            helperText={
              thresholdError
                ? t('customerSignature.thresholdInvalid')
                : t('customerSignature.thresholdHint')
            }
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              },
              htmlInput: { inputMode: 'decimal' },
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.requireForConsumers}
                onChange={(event) => patchForm({ requireForConsumers: event.target.checked })}
                disabled={loading || !conditional}
                slotProps={{ input: { 'aria-label': t('customerSignature.requireForConsumers') } }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">{t('customerSignature.requireForConsumers')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('customerSignature.requireForConsumersHint')}
                </Typography>
              </Box>
            }
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('customerSignature.signerRoles')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('customerSignature.signerRolesHint')}
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label={t('customerSignature.roleOrderer')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.allowBillingPartySigner}
                    onChange={(event) =>
                      patchForm({ allowBillingPartySigner: event.target.checked })
                    }
                    disabled={loading}
                    slotProps={{ input: { 'aria-label': t('customerSignature.roleBillingParty') } }}
                  />
                }
                label={t('customerSignature.roleBillingParty')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.allowDriverSigner}
                    onChange={(event) => patchForm({ allowDriverSigner: event.target.checked })}
                    disabled={loading}
                    slotProps={{ input: { 'aria-label': t('customerSignature.roleDriver') } }}
                  />
                }
                label={t('customerSignature.roleDriver')}
              />
            </FormGroup>
          </Box>

          <TextField
            label={t('customerSignature.consentText')}
            value={form.consentText}
            onChange={(event) => patchForm({ consentText: event.target.value })}
            disabled={loading}
            multiline
            minRows={4}
            helperText={t('customerSignature.consentVersion', { version: nextConsentVersion })}
          />

          <TextField
            label={t('customerSignature.activeFrom')}
            type="date"
            value={form.activeFrom}
            onChange={(event) => patchForm({ activeFrom: event.target.value })}
            disabled={loading}
            helperText={t('customerSignature.activeFromHint')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Paper>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => void handleSave()}
          disabled={saving || loading}
        >
          {saving ? t('saving') : t('saveChanges')}
        </Button>
      </Box>
    </Box>
  );
}
